import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCalendarAlt, FaMapMarkerAlt, FaClock, FaTimes, FaChevronLeft, FaChevronRight, FaImages } from 'react-icons/fa';
import './Events.css';

import { API_BASE_URL } from "../apiConfig";

const Events = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('upcoming');
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [upcomingEvents, setUpcomingEvents] = useState([]);
    const [pastEvents, setPastEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lightboxIndex, setLightboxIndex] = useState(null);

    const getGoogleCalendarLink = (event) => {
        if (!event) return "#";
        const title = encodeURIComponent(event.title);
        const details = encodeURIComponent(event.description || event.title);
        const location = encodeURIComponent(event.location || '');
        
        const startDate = new Date(event.rawDate);
        const endDate = new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
        const formatGDate = (date) => date.toISOString().replace(/-|:|\.\d+/g, "").split('.')[0] + "Z";

        return `https://www.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${formatGDate(startDate)}/${formatGDate(endDate)}&details=${details}&location=${location}`;
    };

    const openLightbox = (index) => setLightboxIndex(index);
    const closeLightbox = () => setLightboxIndex(null);

    const showPrev = (e) => {
        e.stopPropagation();
        setLightboxIndex(prev => (prev === 0 ? selectedEvent.images.length - 1 : prev - 1));
    };

    const showNext = (e) => {
        e.stopPropagation();
        setLightboxIndex(prev => (prev === selectedEvent.images.length - 1 ? 0 : prev + 1));
    };

    useEffect(() => {
        const fetchEvents = async () => {
            setLoading(true);
            try {
                const [upcomingRes, pastRes] = await Promise.all([
                    fetch(`${API_BASE_URL}/api/events/upcoming`),
                    fetch(`${API_BASE_URL}/api/events/past`)
                ]);

                const upcomingData = await upcomingRes.json();
                const pastData = await pastRes.json();

                const mapEvent = (event) => ({
                    id: event.EventId,
                    title: event.Title,
                    rawDate: event.EventDate,
                    date: new Date(event.EventDate).toLocaleDateString('en-AU', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                    }),
                    time: new Date(event.EventDate).toLocaleTimeString('en-AU', {
                        hour: '2-digit',
                        minute: '2-digit'
                    }),
                    location: event.Location,
                    image: event.ImageFile 
                        ? `${API_BASE_URL}/uploads/${event.ImageFile.split(',')[0]}` 
                        : 'https://images.unsplash.com/photo-1593113630400-ea4288922497',
                    images: event.ImageFile 
                        ? event.ImageFile.split(',').filter(img => img.trim() !== "").map(img => `${API_BASE_URL}/uploads/${img}`) 
                        : [],
                    description: event.Description,
                    status: new Date(event.EventDate) < new Date() ? 'Past' : 'Upcoming',
                    registrationOpen: event.RegistrationEnabled,
                    galleryId: event.GalleryEventId
                });

                const now = new Date();
                const allFetchedEvents = [
                    ...upcomingData.map(mapEvent),
                    ...pastData.map(mapEvent)
                ];

                // Remove duplicates by ID (in case an event is in both lists)
                const uniqueEvents = Array.from(new Map(allFetchedEvents.map(e => [e.id, e])).values());

                const newlyUpcoming = uniqueEvents.filter(e => new Date(e.rawDate) >= now);
                const newlyPast = uniqueEvents.filter(e => new Date(e.rawDate) < now);

                setUpcomingEvents(newlyUpcoming);
                setPastEvents(newlyPast);
            } catch (error) {
                console.error("Error fetching events:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchEvents();
    }, []);

   

    // View Details Handler
    if (selectedEvent) {
        return (
            <div className="events-page-container">
                <div className="event-detail-modal">
                    <h2>{selectedEvent.title}</h2>
                    <img src={selectedEvent.image} alt={selectedEvent.title} style={{ width: '100%', maxHeight: '450px', objectFit: 'cover', borderRadius: '16px', marginBottom: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                    
                    <div className="event-detail-meta-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', background: '#f8fafc', padding: '20px', borderRadius: '16px', marginBottom: '30px' }}>
                        <div className="meta-item">
                            <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: '800', color: '#64748b', display: 'block', marginBottom: '4px' }}>Date & Time</label>
                            <p style={{ margin: 0, fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}><FaCalendarAlt style={{ color: '#007bff' }} /> {selectedEvent.date} | <FaClock style={{ color: '#007bff' }} /> {selectedEvent.time}</p>
                        </div>
                        <div className="meta-item">
                            <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: '800', color: '#64748b', display: 'block', marginBottom: '4px' }}>Location</label>
                            <p style={{ margin: 0, fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}><FaMapMarkerAlt style={{ color: '#f44336' }} /> {selectedEvent.location}</p>
                        </div>
                    </div>

                    <div style={{ marginBottom: '40px' }}>
                        <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: '800', color: '#64748b', display: 'block', marginBottom: '12px' }}>Description</label>
                        <p style={{ textAlign: 'left', lineHeight: '1.8', color: '#334155', fontSize: '1rem' }}>{selectedEvent.description}</p>
                    </div>

                    {selectedEvent.images && selectedEvent.images.length > 1 && (
                        <div style={{ marginBottom: '40px' }}>
                            <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: '800', color: '#64748b', display: 'block', marginBottom: '15px' }}>Event Gallery</label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '15px' }}>
                                {selectedEvent.images.map((img, i) => (
                                    <div key={i} className="gallery-item" style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                        <img src={img} alt={`Gallery ${i}`} style={{ width: '100%', height: '120px', objectFit: 'cover', cursor: 'pointer', transition: 'transform 0.3s' }} onClick={() => openLightbox(i)} onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'} onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div style={{ marginTop: '40px', padding: '20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            {selectedEvent.registrationOpen ? (
                                <button className="view-details-btn">Register Now</button>
                            ) : selectedEvent.status === 'Upcoming' ? (
                                <p style={{ color: '#ef4444', fontWeight: 'bold', margin: 0 }}>Registration Closed</p>
                            ) : null}

                            {selectedEvent.galleryId && (
                                <button 
                                    className="view-details-btn" 
                                    onClick={() => navigate(`/gallery/${selectedEvent.galleryId}`)}
                                    style={{ marginLeft: '12px', background: '#4c1d95' }}
                                >
                                    <FaImages style={{ marginRight: '8px' }} />
                                    View Gallery
                                </button>
                            )}

                            {selectedEvent.status === 'Upcoming' && (
                                <a 
                                    href={getGoogleCalendarLink(selectedEvent)} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: 'none', marginLeft: '12px' }}
                                >
                                    <button 
                                        className="back-btn" 
                                        style={{ 
                                            background: '#fff7ed', 
                                            color: '#ea580c', 
                                            border: '1px solid #fed7aa',
                                            padding: '10px 24px',
                                            borderRadius: '10px',
                                            fontWeight: '600',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <FaCalendarAlt style={{ marginRight: '8px' }} />
                                        Add to Calendar
                                    </button>
                                </a>
                            )}
                        </div>
                        <button className="back-btn" onClick={() => setSelectedEvent(null)} style={{ border: 'none', background: '#f1f5f9', color: '#475569', padding: '10px 24px', borderRadius: '10px', fontWeight: '600', cursor: 'pointer' }}>Back to Events list</button>
                    </div>

                    {/* Lightbox */}
                    {lightboxIndex !== null && (
                        <div className="event-lightbox-overlay" onClick={closeLightbox}>
                            <div className="event-lightbox-content" onClick={e => e.stopPropagation()}>
                                <button className="event-lightbox-close" onClick={closeLightbox}><FaTimes /></button>
                                <button className="event-lightbox-nav prev" onClick={showPrev}><FaChevronLeft /></button>
                                <img src={selectedEvent.images[lightboxIndex]} alt={`Gallery ${lightboxIndex}`} />
                                <button className="event-lightbox-nav next" onClick={showNext}><FaChevronRight /></button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="events-page-container">
            <h1 className="events-page-title">MKS Events</h1>
            <div className="events-divider"></div>

            {/* Admin Panel Redesign */}
            {/* <div className="events-admin-panel">
                <div className="admin-header">
                    <div className="admin-header-title">
                        <FaCog className="admin-icon-main" />
                        <div>
                            <h3>Admin Dashboard</h3>
                            <p className="admin-subtitle">Manage MKS events, statuses, and registrations</p>
                        </div>
                    </div>
                </div>

                <div className="admin-actions-grid">
                    <button className="admin-card-btn primary" onClick={() => handleAdminAction('Create New Event')}>
                        <div className="icon-wrapper"><FaPlus /></div>
                        <div className="btn-text">
                            <span className="btn-title">Create Event</span>
                            <span className="btn-desc">Add a new upcoming event</span>
                        </div>
                    </button>

                    <button className="admin-card-btn secondary" onClick={() => handleAdminAction('Edit Event Details/Status')}>
                        <div className="icon-wrapper"><FaEdit /></div>
                        <div className="btn-text">
                            <span className="btn-title">Edit Details</span>
                            <span className="btn-desc">Modify event descriptions</span>
                        </div>
                    </button>

                    <button className="admin-card-btn secondary" onClick={() => handleAdminAction('Add/Change Image')}>
                        <div className="icon-wrapper"><FaEdit /></div>
                        <div className="btn-text">
                            <span className="btn-title">Update Image</span>
                            <span className="btn-desc">Add or change event flyer</span>
                        </div>
                    </button>

                    <button className="admin-card-btn outline" onClick={() => handleAdminAction('Change Status (Upcoming/Past)')}>
                        <div className="icon-wrapper"><FaCog /></div>
                        <div className="btn-text">
                            <span className="btn-title">Set Status</span>
                            <span className="btn-desc">Move to Past/Upcoming</span>
                        </div>
                    </button>

                    <button className="admin-card-btn outline" onClick={() => handleAdminAction('Toggle Registration')}>
                        <div className="icon-wrapper"><FaCog /></div>
                        <div className="btn-text">
                            <span className="btn-title">Registration</span>
                            <span className="btn-desc">Enable or disable forms</span>
                        </div>
                    </button>
                </div>
            </div> */}

            <div className="events-navigation">
                <button
                    className={activeTab === 'upcoming' ? 'active' : ''}
                    onClick={() => setActiveTab('upcoming')}
                >
                    Section 1: Upcoming Events
                </button>
                <button
                    className={activeTab === 'past' ? 'active' : ''}
                    onClick={() => setActiveTab('past')}
                >
                    Section 2: Past Events
                </button>
            </div>

            <div className="events-content">
                <h2 className="events-section-title">
                    {activeTab === 'upcoming' ? 'Upcoming Events' : 'Past Events'}
                </h2>

                <div className="events-grid">
                    {loading ? (
                        <p>Loading events...</p>
                    ) : (
                        (activeTab === 'upcoming' ? upcomingEvents : pastEvents).map(event => (
                            <div key={event.id} className="event-card-wrapper">
                                <img src={event.image} alt={event.title} className="event-image" />

                                <div className="event-details">
                                    <span className={`event-status ${event.status === 'Upcoming' ? 'status-upcoming' : 'status-past'}`}>
                                        {event.status}
                                    </span>
                                    <h3 className="event-title">{event.title}</h3>
                                    <div className="event-meta">
                                        <FaCalendarAlt /> <span>{event.date}</span>
                                    </div>
                                    <div className="event-meta">
                                        <FaMapMarkerAlt /> <span>{event.location}</span>
                                    </div>

                                    <p className="event-description">{event.description}</p>

                                    <div className="event-footer">
                                        <button className="view-details-btn" onClick={() => setSelectedEvent(event)}>
                                            Event detail page
                                        </button>

                                        {event.status === 'Upcoming' && (
                                            <span className={`registration-badge ${event.registrationOpen ? 'reg-open' : 'reg-closed'}`}>
                                                {event.registrationOpen ? 'Registration: Open' : 'Registration: Disabled'}
                                            </span>
                                        )}

                                        {event.galleryId && (
                                            <button 
                                                className="view-details-btn" 
                                                onClick={(e) => { e.stopPropagation(); navigate(`/gallery/${event.galleryId}`); }}
                                                style={{ marginTop: '10px', width: '100%', background: '#4c1d95' }}
                                            >
                                                <FaImages style={{ marginRight: '8px' }} />
                                                View Event Gallery
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}

                    {(!loading && activeTab === 'upcoming' && upcomingEvents.length === 0) && (
                        <p>No upcoming events at the moment.</p>
                    )}
                    {(!loading && activeTab === 'past' && pastEvents.length === 0) && (
                        <p>No past events recorded.</p>
                    )}
                </div>
            </div>

        </div>
    );
};

export default Events;